import { AxiosError, type AxiosInstance } from 'axios'

import { env } from '@/src/config/env'

type TErrorCallback = (error: Pick<AxiosError, 'code' | 'message'>) => void

let errorCounter = 0

const errorNetworkListeners: TErrorCallback[] = []

export const addErrorListener = (callback: TErrorCallback) => {
  errorNetworkListeners.push(callback)
  return () => {
    // Remove the callback from the listeners array
    errorNetworkListeners.splice(errorNetworkListeners.indexOf(callback), 1)
  }
}

/**
 * Notify all the registered error listeners with the given error.
 *
 * @param {object} error - An object with `code` and `message` properties.
 * @param {string} error.code - The error code.
 * @param {string} error.message - The error message.
 */
export const notifyListeners = (error: Pick<AxiosError, 'code' | 'message'>) => {
  errorNetworkListeners.forEach((callback) => callback(error))
}

const blackListUrls = [/status/, /notifications/, /conversation/]

const doesNotMatchBlackListedUrls = (input: string): boolean => {
  return !blackListUrls.some((regex) => regex.test(input))
}

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

// Reads the Sentry XHR breadcrumb URL off the Axios request, with runtime guards.
const extractSentryUrl = (request: unknown): string | undefined => {
  if (!isObject(request)) return undefined
  const xhr = request.__sentry_xhr_v3__
  if (!isObject(xhr)) return undefined
  return typeof xhr.url === 'string' ? xhr.url : undefined
}

/**
 * Add an interceptor to the given Axios instance that listens for network errors.
 *
 * When such an error occurs, it notifies all the registered error listeners with
 * the given error.
 *
 * Component listening to networkError :
 * - @see src/general/components/ServiceUnavailable
 *
 * @param {object} axiosInstance - The Axios instance to add the interceptor to.
 */
export const setNetworkErrorInterceptor = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.response.use(undefined, (error: Error) => {
    if (error instanceof AxiosError) {
      const request: unknown = error.request
      const url = extractSentryUrl(request)
      if (
        error.code === 'ERR_NETWORK' &&
        env.EXPO_PUBLIC_APP_BASE_URL &&
        typeof url === 'string' &&
        url.startsWith(env.EXPO_PUBLIC_APP_BASE_URL) &&
        doesNotMatchBlackListedUrls(url)
      ) {
        errorCounter += 1
        if (errorCounter === 3) {
          notifyListeners({
            code: error.code,
            message: error.message,
          })
        }
      } else {
        errorCounter = 0
      }
    }
    return Promise.reject(error)
  })
}
